import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import { grey } from "@mui/material/colors";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { debounce } from "lodash";

import fpStockResultService from "@plannel/services/fp/fp-stock-result-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, { buildFlatReportStatusBar, REPORT_TREE_GRID_OPTIONS } from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old SearchOptsGroup 필드 2개 — StockResultQueryRepository의 makeWhereClause가 itemCdNm/stockCdNm
// 둘 다 contains 조건으로 소비(old MainGrid.loadData 시그니처 기준).
const SEARCH_FIELD_LABELS = { itemCdNm: "FP_ITEM", stockCdNm: "FP_STOCK" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

// old MainGrid 클릭 시 이동 대상(Stock 화면) — fp-master-screen-guide.md §13 historyState 패턴.
// TabMenuList.js의 reduxKey/viewName과 반드시 같아야 한다.
const STOCK_VIEW_REDUX_KEY = "FP_STOCK";

// 행 그룹(트리) 계층 — 공장 > 품목 구분 > 품목 그룹(2026-08-12 사용자 지시). 이 화면의 데이터
// 그레인은 stockCd(재고 단위)라 위 3개는 여러 재고 행에 반복되는 상위 식별 컬럼이고, 그룹으로
// 접으면 실제로 여러 leaf가 묶인다(§A-6b "그레인 자체는 rowGroup에 넣지 않는다" 기준 통과).
// 공장/품목그룹은 "(코드) 명칭" 합성 표시라 코드 필드만으로는 그룹 키에서 명칭을 복원할 수 없어
// 조회 시점에 합성 문자열 필드(plantLabel/itemParentGrpLabel)를 만들어 그걸로 그룹한다.
// 품목 구분(itemClassCd)은 코드 자체로 그룹하고 표시 컬럼의 valueFormatter가 번역한다 —
// 번역문으로 그룹하면 언어 전환 시 그룹 키가 통째로 바뀌기 때문.
const GROUP_FIELDS = ["plantLabel", "itemClassCd", "itemParentGrpLabel"];

const StockResult = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [onHandOnly, setOnHandOnly] = useState(true);

  const itemCdNmRef = useRef("");
  const stockCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const detailGridRef = useRef();
  const [mainRowData, setMainRowData] = useState([]);
  const [detailRowData, setDetailRowData] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);

  const latestReqRef = useRef(0);
  const latestDetailReqRef = useRef(0);
  const searchStateRef = useRef({ versionState, onHandOnly });
  searchStateRef.current = { versionState, onHandOnly };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveStockSupply(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detailGridMemo = useMemo(() => {
    const base = FpGridSetting({ title: `${title}-detail`, viewName: `${viewName}_DETAIL` });
    return { ...base, defaultColDef: { ...base.defaultColDef, editable: false } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old MainGrid onCellClicked — stockCd/onHandYn을 꺼내 상세 그리드 재조회 트리거(모든 데이터
  // 셀 클릭에서 동작, stockCd 컬럼에 한정하지 않음) + onHandYn===true일 때만 Stock 화면 딥링크.
  const onMainRowClicked = (params) => {
    const row = params.data;
    if (!row) return;
    setSelectedStock({ stockCd: row.stockCd, onHandYn: row.onHandYn });
  };

  // old history.push({pathname: getViewPath('UI_FP_STOCK'), state: {stockCd, planScope}}) 대응 —
  // new는 SPA 탭이라 §13 historyState 패턴(viewStates 선반영 + historyState 1회성 신호)으로 대체.
  // TODO(MIG-T2): [stockresult/StockResult] Stock 화면 진입 시 자동조회 미완성 — 수신측
  //   (`master/Stock.js`)이 이 신호를 아직 구독하지 않는다(Stock.js 자체에 이미 등재된
  //   TODO(MIG-T2) "location.state 딥링크 자동조회 미이관"과 동일 건, 이 화면에서 중복 등재하지 않음).
  //   Stock.js가 §13-2 수신 로직을 갖추면 이 dispatch만으로 바로 연결된다.
  const onStockCdClicked = (row) => {
    if (!row?.onHandYn) return;
    const { planScope } = searchStateRef.current.versionState;
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(STOCK_VIEW_REDUX_KEY, { planScope, stockCdNm: row.stockCd })));
    reduxDispatch(reduxUtil.updateHistoryState(STOCK_VIEW_REDUX_KEY));
  };

  // old MainGrid columnProperty(valueCallback으로 "(코드) 명칭" 합성) — 별도 코드/명칭 컬럼을
  // 나란히 두지 않고 하나의 표시 컬럼으로 합친다(old도 코드 컬럼 자체는 숨김, 합성 표시만 함).
  const composeCodeName = (code, name) => (code || name ? `(${code ?? ""}) ${name ?? ""}` : "");

  const codeNameFormatter = (codeField, nameField) => (params) => (
    composeCodeName(params.data?.[codeField], params.data?.[nameField])
  );

  // 행 그룹 키로 쓸 합성 문자열을 조회 시점에 만들어 둔다(위 GROUP_FIELDS 주석 참고).
  const buildMainRowData = (rows) => (rows ?? []).map(row => ({
    ...row,
    plantLabel: composeCodeName(row.plantCd, row.plantNm),
    itemParentGrpLabel: composeCodeName(row.itemParentGrpCd, row.itemParentGrpNm),
  }));

  // old mainGridItems 1:1 대조 — itemDetailGroup(plantNm~inventoryNm, 숨김 코드필드 4개 포함)
  // + supplyGroup(stockCd~onHandYn). horizontal column group의 groupShowMode "always"/"expand"는
  // AG Grid 네이티브 컬럼 그룹의 `columnGroupShow`(없음=always, "open"=expand)로 그대로 대응된다.
  const mainColumnDefs = useMemo(() => [
    // 실제 그룹 기준 컬럼 — 화면엔 숨기고 계층만 만든다(§A-6b, 배열 순서 = 그룹 depth).
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),
    {
      headerName: "FP_ITEM_DETAIL", groupId: "itemDetailGroup", marryChildren: true,
      children: [
        // 공장/품목구분/품목그룹은 showRowGroup 표시 컬럼(펼침·접힘 아이콘 포함)이다. old의
        // columnGroupShow:"open"(컬럼그룹 접으면 숨김)을 그대로 유지해 초기엔 품목구분/품목만 보인다
        // (old 컬럼정의와 동일 — 공장/품목그룹/인벤토리가 "expand" 대상). 트리 표시 컬럼이 숨겨져도
        // REPORT_TREE_GRID_OPTIONS의 groupDefaultExpanded:-1(전부 펼침)+groupHideOpenParents 조합이라
        // 데이터 행은 그대로 다 보이고, 보이는 표시 컬럼(품목구분)으로 계층 접기/펼치기도 계속 된다 —
        // 다만 숨겨진 레벨(공장/품목그룹)은 컬럼그룹을 펼치기 전까지 개별 접기가 불가능하다.
        {
          headerName: "FP_PLANT", colId: "plantNmGrp", showRowGroup: "plantLabel", width: 200,
          columnGroupShow: "open",
          cellRenderer: "agGroupCellRenderer", cellRendererParams: { suppressCount: true },
          type: ["nonEditableColumn"]
        },
        {
          // 코드값(P/I/M)이 아니라 번역 라벨(완제품/반제품/원자재)을 보여준다 — 그룹 키는 코드로
          // 두고(언어 전환 시 그룹이 재구성되지 않도록) 표시만 번역한다.
          // ⚠️ 그룹 표시 컬럼은 colDef.valueFormatter가 셀 화면 표시에 적용되지 않는다
          // (agGroupCellRenderer가 그룹 키를 직접 그림) — AG Grid 35 문서상 그룹 셀 내용 커스터마이즈는
          // cellRendererParams.innerRenderer가 정식 경로다(OrderTracking.js와 동일 패턴).
          // valueFormatter는 화면이 아니라 엑셀 export/클립보드용으로 같이 남겨둔다.
          headerName: "FP_ITEM_CLASS_CD", colId: "itemClassCdGrp", showRowGroup: "itemClassCd", width: 130,
          cellRenderer: "agGroupCellRenderer",
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: (p) => (p.value ? t(`FP_ITEM_CLASS_CD_${p.value}`) : "")
          },
          type: ["nonEditableColumn", "centerAligned"],
          valueFormatter: (p) => (p.value ? t(`FP_ITEM_CLASS_CD_${p.value}`) : "")
        },
        {
          headerName: "FP_ITEM_GRP", colId: "itemParentGrpNmGrp", showRowGroup: "itemParentGrpLabel", width: 220,
          columnGroupShow: "open",
          cellRenderer: "agGroupCellRenderer", cellRendererParams: { suppressCount: true },
          type: ["nonEditableColumn"]
        },
        { headerName: "FP_ITEM", field: "itemNm", colId: "itemNm", width: 300, valueFormatter: codeNameFormatter("itemCd", "itemNm") },
        { headerName: "FP_INVENTORY", field: "inventoryNm", colId: "inventoryNm", width: 220, columnGroupShow: "open", valueFormatter: codeNameFormatter("inventoryCd", "inventoryNm") },
        { field: "plantCd", colId: "plantCd", hide: true },
        { field: "inventoryCd", colId: "inventoryCd", hide: true },
        { field: "itemCd", colId: "itemCd", hide: true },
        { field: "itemParentGrpCd", colId: "itemParentGrpCd", hide: true }
      ]
    },
    {
      headerName: "FP_STOCK", groupId: "supplyGroup", marryChildren: true,
      children: [
        {
          headerName: "STOCK_CD", field: "stockCd", colId: "stockCd", width: 190,
          cellStyle: (params) => (params.data?.onHandYn ? { color: "#1976d2", textDecoration: "underline", cursor: "pointer" } : null)
        },
        { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", width: 190, columnGroupShow: "open" },
        { headerName: "USABLE_DATE", field: "usableTs", colId: "usableTs", width: 180, type: ["dateTimeColumn", "centerAligned"], columnGroupShow: "open" },
        { headerName: "AVAIL_QTY", field: "usableQty", colId: "usableQty", width: 120, type: ["numberColumn", "rightAligned"] },
        { headerName: "FP_USED_QTY", field: "usedQty", colId: "usedQty", width: 120, type: ["numberColumn", "rightAligned"] },
        { headerName: "FP_REMAIN_QTY", field: "remainQty", colId: "remainQty", width: 120, type: ["numberColumn", "rightAligned"] },
        { headerName: "EXPIRE_DATE", field: "expireTs", colId: "expireTs", width: 180, type: ["dateTimeColumn", "centerAligned"], columnGroupShow: "open" },
        { headerName: "FP_CREATED_BY", field: "inputWoCd", colId: "inputWoCd", width: 190, columnGroupShow: "open" },
        { field: "onHandYn", colId: "onHandYn", hide: true, type: ["booleanColumn"] }
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old detailGridItems 1:1 대조 — consumptionGroup(usedTs~peggingYn), stockCd는 로드 시점에
  // 이미 단일 stockCd로 필터링돼 화면에 보일 필요가 없어 숨김만 유지.
  const detailColumnDefs = useMemo(() => [
    {
      headerName: "FP_CONSUMPTION", groupId: "consumptionGroup", marryChildren: true,
      children: [
        { field: "stockCd", colId: "stockCd", hide: true },
        // 하단 합계 고정행(pinnedBottomRowData)의 "합계 :" 라벨이 이 컬럼에 들어온다(old
        // DetailGrid의 groupFooter 라벨 위치와 동일) — `dateTimeColumn` 타입의
        // valueFormatter(`dateUtils.formatDateTime`)는 날짜로 파싱 안 되는 값을 빈 문자열로
        // 만들어(`date-util.js:29`) **라벨을 조용히 삼킨다.** 고정행만 원문을 그대로 통과시킨다
        // (같은 트랙 `report/ResourceTargetUtilization.js:202`의 rowPinned 가드와 동일 패턴).
        // 합계 라벨은 old처럼 **우측 정렬**해서 바로 오른쪽 칸의 합계 숫자에 붙여 읽히게 한다.
        // `centerAligned` 타입의 cellStyle을 colDef.cellStyle 함수가 통째로 덮으므로(타입보다
        // colDef가 우선) 일반 행의 가운데 정렬은 여기서 다시 지정해야 한다.
        { headerName: "FP_USED_TS", field: "usedTs", colId: "usedTs", width: 180, type: ["dateTimeColumn", "centerAligned"],
          valueFormatter: (params) => (params.node?.rowPinned ? params.value : dateUtils.formatDateTime(params)),
          cellStyle: (params) => ({
            display: "flex", alignItems: "center",
            justifyContent: params.node?.rowPinned ? "flex-end" : "center"
          }) },
        { headerName: "FP_USED_QTY", field: "usedQty", colId: "usedQty", width: 120, type: ["numberColumn", "rightAligned"] },
        { headerName: "FP_WORK_ORDER_CODE", field: "woCd", colId: "woCd", width: 220 },
        { headerName: "FP_SO_CD", field: "dmndCd", colId: "dmndCd", width: 190, columnGroupShow: "open" },
        { headerName: "FP_PEGGING_YN", field: "peggingYn", colId: "peggingYn", width: 120, type: ["booleanColumn", "centerAligned"] }
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const retrieveStockSupply = () => {
    const { versionState: version, onHandOnly: handOnly } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      itemCdNm: itemCdNmRef.current,
      stockCdNm: stockCdNmRef.current,
      ...(handOnly ? { onHandYn: true } : {})
    };

    gridRef.current?.api?.setGridOption("loading", true);

    fpStockResultService.getStockSupply(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const rows = buildMainRowData(response.data);
        setMainRowData(rows);
        setSelectedStock(null);
        setDetailRowData([]);

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setMainRowData([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveStockConsumption = (stock) => {
    const { versionState: version } = searchStateRef.current;

    const reqId = ++latestDetailReqRef.current;
    const params = {
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      stockCd: stock.stockCd
    };

    detailGridRef.current?.api?.setGridOption("loading", true);

    fpStockResultService.getStockConsumption(params)
      .then(response => {
        if (reqId !== latestDetailReqRef.current) return;
        setDetailRowData(response.data ?? []);
      })
      .catch(e => {
        if (reqId === latestDetailReqRef.current) setDetailRowData([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestDetailReqRef.current) detailGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // old DetailGrid.groupBy(['stockCd'])+groupFooter(합계) — 상세 그리드는 이미 단일 stockCd로만
  // 채워지므로 그룹 트리 대신 하단 고정행(pinnedBottomRowData) 합계 한 줄로 재현한다.
  useEffect(() => {
    const api = detailGridRef.current?.api;
    if (!api) return;

    if (detailRowData.length === 0) {
      api.setGridOption("pinnedBottomRowData", []);
      return;
    }

    const sum = detailRowData.reduce((acc, row) => acc + (row.usedQty ?? 0), 0);
    api.setGridOption("pinnedBottomRowData", [{ usedTs: `${t("SUM")} :`, usedQty: sum }]);
  }, [detailRowData, t]);

  const onMainGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: mainColumnDefs, viewName, initState: true });
  };

  const onDetailGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: detailColumnDefs, viewName: `${viewName}_DETAIL`, initState: true });
  };

  const onMainCellClicked = (params) => {
    // 행 그룹(트리) 전환 후로는 그룹 대표 행에도 클릭이 들어온다 — 그룹 행엔 params.data가 없어
    // 딥링크/상세조회 대상이 아니다(onMainRowClicked도 자체 가드가 있지만 여기서 먼저 걸러낸다).
    if (!params.data) return;

    // 재고 마스터가 권한 밖이면 딥링크는 건너뛰고 행 선택(상세 조회)만 한다.
    if (params.column?.getColId() === "stockCd" && isPermitted(STOCK_VIEW_REDUX_KEY)) {
      onStockCdClicked(params.data);
    }
    onMainRowClicked(params);
  };

  const statusBar = useMemo(() => buildFlatReportStatusBar(), []);

  useEffect(() => {
    if (!versionState.versionCd) {
      debouncedRetrieveRef.current.cancel();
      setMainRowData([]);
      setSelectedStock(null);
      setDetailRowData([]);
      return;
    }

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd, versionState.plantCd, onHandOnly]);

  useEffect(() => {
    if (!selectedStock?.stockCd) {
      setDetailRowData([]);
      return;
    }
    retrieveStockConsumption(selectedStock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ itemCdNm: itemCdNmRef, stockCdNm: stockCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveStockSupply}
        />
        <FormControlLabel
          sx={{ ml: 1.5 }}
          control={<Checkbox size="small" checked={onHandOnly} onChange={(e) => setOnHandOnly(e.target.checked)} />}
          label={t("FP_VIEW_ONLY_ON_HAND")}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      {/* old ResultArea sizes=[65,35] direction="horizontal" — 좌우 2그리드는 이 코드베이스에
          SplitterContainer 선례가 없는(전부 상/하 분할) 새 레이아웃이라 flex Box로 직접 구현
          (fp-report-screen-guide.md §C 신설 시 이 판단 근거를 옮겨 적을 것). */}
      <Box sx={{ display: "flex", gap: 1, height: "calc(100% - 8px)", mt: 1 }}>
        <Box sx={{ flex: "0 0 65%", minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* old MainGrid의 `<ButtonArea>`는 제목이 없었지만(엑셀 버튼만) 우측 DetailGrid의
              ButtonArea와 같은 높이의 바를 차지해 두 그리드 상단이 맞았다 — new도 라벨 텍스트는
              없이 같은 높이(§15-1a의 minHeight 40 + mb 1)의 빈 줄만 둬서 상단을 맞춘다. */}
          <Box sx={{ mb: 1, minHeight: 40 }} />
          <Box id="fp-stock-result-main-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={mainRowData}
              {...defaultGridMemo}
              statusBar={statusBar}
              onGridReady={onMainGridReady}
              onCellClicked={onMainCellClicked}
            />
          </Box>
        </Box>
        <Box sx={{ flex: "0 0 35%", minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* old DetailGrid의 ButtonArea title은 `재고 코드 : <값>`을 한 문자열로 이어 붙였는데,
              그러면 어디까지가 제목인지 구분되지 않는다 — 제목(굵기 500) + 구분선 + 값(보조색)으로
              나눈다(§15-1a, master/Bom.js와 동일 형태). 구분선/값은 실제 선택된 재고가 있을 때만. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, minHeight: 40, minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{t("STOCK_CD")}</Box>
            {selectedStock?.stockCd && (
              <>
                <Divider orientation="vertical" sx={{ height: 16, alignSelf: "center", borderColor: grey[400] }} />
                <Box
                  component="span"
                  sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {selectedStock.stockCd}
                </Box>
              </>
            )}
          </Box>
          <Box id="fp-stock-result-detail-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
            <AgGridReact
              ref={detailGridRef}
              rowData={detailRowData}
              {...detailGridMemo}
              onGridReady={onDetailGridReady}
            />
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(StockResult);
